/**
 * Landing page redirect utility
 * Handles redirecting users to app.youwo.ai with context parameters
 * so the app can show contextual login messages
 */

export type RedirectReason =
  | 'try_feature'
  | 'send_message'
  | 'select_model'
  | 'attach_file'
  | 'voice_input';

export type FeatureAction =
  | 'file_summary'
  | 'youtube_summary'
  | 'realtime_recording'
  | 'image_note'
  | 'audio_transcription'
  | 'post_recording'
  | 'link_parsing'
  | 'generate_quiz'
  | 'generate_flashcard'
  | 'chat'
  | 'attach_file'
  | 'select_model'
  | 'voice_input';

export interface RedirectParams {
  action: FeatureAction;
  redirect_reason: RedirectReason;
  message?: string;
  feature?: string;
}

const APP_BASE_URL = 'https://app.youwo.ai';

/**
 * Build a link to app.youwo.ai, forwarding the ?via= Rewardful referral param if present.
 * This preserves affiliate attribution when users cross from youwo.ai to app.youwo.ai.
 */
export const buildAppLink = (path: string = ''): string => {
  const via = new URLSearchParams(window.location.search).get('via');
  const url = `${APP_BASE_URL}${path}`;
  return via ? `${url}${path.includes('?') ? '&' : '?'}via=${via}` : url;
};

/**
 * Redirect to the main app with context parameters
 * @param params - Redirect parameters including action and reason
 */
export const redirectToApp = (params: RedirectParams): void => {
  const queryParams: Record<string, string> = {
    action: params.action,
    redirect_reason: params.redirect_reason,
  };

  if (params.message) {
    queryParams.message = params.message;
  }

  if (params.feature) {
    queryParams.feature = params.feature;
  }

  // Forward ?via= for Rewardful affiliate tracking across domains
  const via = new URLSearchParams(window.location.search).get('via');
  if (via) queryParams.via = via;

  const queryString = new URLSearchParams(queryParams).toString();
  window.location.href = `${APP_BASE_URL}?${queryString}`;
};

/**
 * Redirect for feature chip clicks
 */
export const redirectForFeature = (featureId: FeatureAction): void => {
  redirectToApp({
    action: featureId,
    redirect_reason: 'try_feature',
    feature: featureId,
  });
};

/**
 * Redirect for chat message send
 */
export const redirectForChat = (message?: string): void => {
  redirectToApp({
    action: 'chat',
    redirect_reason: 'send_message',
    message: message,
  });
};

/**
 * Redirect for file attachment
 */
export const redirectForAttachment = (): void => {
  redirectToApp({
    action: 'attach_file',
    redirect_reason: 'attach_file',
  });
};

/**
 * Redirect for model selection
 */
export const redirectForModelSelect = (): void => {
  redirectToApp({
    action: 'select_model',
    redirect_reason: 'select_model',
  });
};

/**
 * Redirect for voice input
 */
export const redirectForVoiceInput = (): void => {
  redirectToApp({
    action: 'voice_input',
    redirect_reason: 'voice_input',
  });
};
